import * as Websocket from '@anycable/web'

import { WEBSOCKETS_URL } from '@/config'
import { getToken } from '@/services/lukita/utils/getAuthorizationHeader'
import { executeGet } from './rest'

export type Consumer = Websocket.ActionCableConsumer
export type Subscription = ReturnType<Consumer['subscriptions']['create']>

export interface Channel {
  [token: string]: boolean | number | string
}

export interface ChannelMixin {
  initialized: () => void
  connected: (reconnected: boolean) => void
  rejected: () => void
  received: (data: unknown) => void
  disconnected: () => void
  [key: string]: unknown
}

const consumers = new Map<string, Promise<Consumer | null>>()

export const fetchCableToken = async () => {
  const response = await executeGet('/cable/token')
  return response.json()
}

const boostrapConsumer = async (): Promise<Consumer | null> => {
  const { token: cableToken } = await fetchCableToken()
  if (!cableToken) return null

  return Websocket.createConsumer(WEBSOCKETS_URL, {
    auth: { token: cableToken },
  })
}

const clearStaleConsumers = async (
  currentConsumer?: Promise<Consumer | null>,
): Promise<void> => {
  const byStaleConsumers = ([, consumer]: [string, Promise<Consumer | null>]) =>
    !currentConsumer || currentConsumer !== consumer

  const clearConsumer = async ([token, consumerPromise]: [
    string,
    Promise<Consumer | null>,
  ]) => {
    const consumer = await consumerPromise
    if (isConsumerActive(consumer)) disconnectConsumer(consumer)
    consumers.delete(token)
  }

  await Promise.all(
    Array.from(consumers.entries()).filter(byStaleConsumers).map(clearConsumer),
  )
}

const isConsumerActive = (consumer: Consumer | null): boolean =>
  // @ts-expect-error
  consumer !== null && consumer.cable.monitor?.state !== 'closed'

export const disconnectConsumer = (consumer: Consumer | null): void => {
  if (consumer) consumer.disconnect()
}

export const subscribe = (
  consumer: Consumer | null,
  channel: Channel,
  mixin: Partial<ChannelMixin>,
): Subscription | undefined => {
  if (!consumer) return
  // @ts-expect-error — ChannelMixin.connected takes (reconnected: boolean) but AnyCable types declare () => void
  return consumer.subscriptions.create(channel, mixin)
}

export const unsubscribe = (subscription: Subscription | undefined): void => {
  subscription?.unsubscribe()
}

export const createConsumer = async (): Promise<Consumer | null> => {
  const authToken = await getToken()
  if (!authToken) {
    await clearStaleConsumers()
    return null
  }

  if (consumers.has(authToken)) {
    const consumer = await consumers.get(authToken)!
    if (isConsumerActive(consumer)) return consumer

    consumers.delete(authToken)
  }

  const consumer = boostrapConsumer()
  consumers.set(authToken, consumer)

  await clearStaleConsumers(consumer)
  return consumer
}
